import { useEffect, useMemo, useRef, useState } from "react";
import { X, Download, AlertTriangle, Loader2, FileText, ExternalLink } from "lucide-react";
import { api } from "@/lib/api/client";
import { useAuthStore } from "@/features/auth/store";
import { Button } from "@/components/ui/button";

type PreviewableFile = {
  fileId: number;
  fileName: string | null;
  filePath?: string | null;
  /** "attach" = legacy ATTACH table; "files" (default) = new files table. */
  source?: "files" | "attach";
};

type Props = {
  open: boolean;
  file: PreviewableFile | null;
  onClose: () => void;
};

type LoadState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "ready"; url: string; mime: string; ext: string }
  | { phase: "error"; message: string };

const INLINE_EXT = ["pdf", "png", "jpg", "jpeg", "gif", "webp"];

export function FilePreviewModal({ open, file, onClose }: Props) {
  const user = useAuthStore((s) => s.user);
  const [load, setLoad] = useState<LoadState>({ phase: "idle" });
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  const ext = useMemo(() => {
    const name = file?.fileName ?? "";
    return name.split(".").pop()?.toLowerCase() ?? "";
  }, [file]);

  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    setLoad({ phase: "loading" });
    setShowDownloadWarning(false);

    if (!INLINE_EXT.includes(ext)) {
      setLoad({
        phase: "error",
        message: `Không thể xem trực tiếp tệp .${ext}. Vui lòng tải về để mở.`,
      });
      return;
    }

    const srcQ = file.source === "attach" ? "?source=attach" : "";
    api
      .get(`/file/${file.fileId}/preview${srcQ}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const blob = res.data as Blob;
        const mime =
          blob.type ||
          res.headers["content-type"] ||
          (ext === "pdf" ? "application/pdf" : "application/octet-stream");
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setLoad({ phase: "ready", url, mime, ext });
      })
      .catch(async (err) => {
        if (cancelled) return;
        // When responseType is "blob", error bodies are Blobs too — unwrap to JSON.
        let message = "Không tải được tệp để xem trước.";
        const data = err?.response?.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            message = parsed?.msg ?? parsed?.message ?? message;
          } catch {
            /* leave default */
          }
        } else if (typeof data === "object" && data) {
          message =
            (data as { msg?: string; message?: string }).msg ??
            (data as { message?: string }).message ??
            message;
        }
        setLoad({ phase: "error", message });
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [open, file, ext]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !file) return null;

  const triggerDownload = async () => {
    try {
      const srcQ = file.source === "attach" ? "?source=attach" : "";
      const res = await api.get(`/file/${file.fileId}/download${srcQ}`, { responseType: "blob" });
      const blob = res.data as Blob;
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = file.fileName ?? `file-${file.fileId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setShowDownloadWarning(false);
    } catch (err) {
      // Blob-typed error body must be unwrapped to JSON to surface the real message.
      let message = "Không tải được tệp.";
      const e = err as { response?: { data?: unknown } };
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed?.msg ?? parsed?.message ?? message;
        } catch {
          /* noop */
        }
      } else if (typeof data === "object" && data) {
        const d = data as { msg?: string; message?: string };
        message = d.msg ?? d.message ?? message;
      }
      setLoad({ phase: "error", message });
      setShowDownloadWarning(false);
    }
  };

  const isImage = load.phase === "ready" && load.mime.startsWith("image/");
  const isPdf = load.phase === "ready" && load.mime === "application/pdf";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex flex-col animate-fade-in"
      onClick={onClose}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 bg-background border-b border-border shadow-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-8 w-8 shrink-0 rounded-md bg-primary-50 text-primary-600 grid place-items-center">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">
            {file.fileName ?? `Tệp #${file.fileId}`}
          </div>
          <div className="text-xs text-muted-foreground truncate hidden sm:block">
            Xem trước · Người mở: <span className="font-medium">{user?.loginName}</span>
            {user?.fullName && <> ({user.fullName})</>}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDownloadWarning(true)}
          disabled={load.phase === "loading"}
          className="shrink-0"
        >
          <Download className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Tải về</span>
        </Button>
        <button
          onClick={onClose}
          className="h-11 w-11 sm:h-8 sm:w-8 grid place-items-center rounded hover:bg-muted shrink-0"
          aria-label="Đóng"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body: preview + watermark overlay */}
      <div
        className="relative flex-1 overflow-hidden bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        {load.phase === "loading" && (
          <div className="absolute inset-0 grid place-items-center text-neutral-200">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Đang tải tệp…</span>
            </div>
          </div>
        )}

        {load.phase === "error" && (
          <div className="absolute inset-0 grid place-items-center text-neutral-200">
            <div className="flex flex-col items-center gap-3 max-w-md text-center px-6">
              <AlertTriangle className="h-8 w-8 text-warning" />
              <div className="text-sm">{load.message}</div>
              <Button variant="outline" size="sm" onClick={() => setShowDownloadWarning(true)}>
                <Download className="h-4 w-4 mr-1.5" /> Tải về để mở bên ngoài
              </Button>
            </div>
          </div>
        )}

        {isPdf && (
          <iframe
            title={file.fileName ?? `file-${file.fileId}`}
            src={`${(load as { phase: "ready"; url: string }).url}#toolbar=0&navpanes=0`}
            className="h-full w-full bg-white"
          />
        )}
        {isImage && (
          <div className="h-full w-full grid place-items-center p-6">
            <img
              src={(load as { phase: "ready"; url: string }).url}
              alt={file.fileName ?? ""}
              className="max-h-full max-w-full object-contain shadow-xl rounded"
            />
          </div>
        )}

        {/* PDF watermark is now embedded in the file bytes server-side; no CSS overlay */}
      </div>

      {/* Footer hint */}
      <div
        className="px-3 sm:px-5 py-2 bg-background border-t border-border text-xs text-muted-foreground flex items-center justify-between gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <ExternalLink className="h-3 w-3 shrink-0" />
          <span className="truncate">
            <span className="hidden sm:inline">Đang xem ở chế độ xem trước — </span>PDF được gắn
            watermark
          </span>
        </span>
        <span className="tabular-nums shrink-0">
          #{file.fileId} · .{ext}
        </span>
      </div>

      {/* Download warning dialog */}
      {showDownloadWarning && (
        <div
          className="absolute inset-0 grid place-items-center p-4 bg-black/40"
          onClick={(e) => {
            e.stopPropagation();
            setShowDownloadWarning(false);
          }}
        >
          <div
            className="max-w-md w-full rounded-lg bg-background border border-border shadow-lifted"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-5">
              <div className="h-10 w-10 shrink-0 rounded-full bg-warning/15 text-warning grid place-items-center">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-base mb-1">Cảnh báo trước khi tải về</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Tệp này chứa thông tin nội bộ. Khi tải về máy, bạn chịu trách nhiệm về việc bảo
                  mật và chỉ được sử dụng cho công việc được giao. Hệ thống sẽ ghi nhận hành động
                  tải cùng thông tin người dùng{" "}
                  <span className="font-medium text-foreground">
                    {user?.loginName} ({user?.fullName ?? "—"})
                  </span>
                  .
                </p>
                <div className="mt-3 rounded bg-muted/50 text-xs p-3 text-muted-foreground">
                  Tệp: <span className="font-medium text-foreground">{file.fileName}</span>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDownloadWarning(false)}>
                Huỷ
              </Button>
              <Button variant="default" size="sm" onClick={triggerDownload}>
                <Download className="h-4 w-4 mr-1.5" /> Tôi hiểu, tải về
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
